<script setup>
// NOTICE: KAPTION-INTEGRATION
// two-step-verification.BACKUP.1 — "Save your backup codes" (canvas:
// BackupCodes). Codes exist only in this dialog: the server keeps digests.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const emit = defineEmits(['closed']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const codes = ref([]);
const addedMethod = ref('');
const saved = ref(false);

const addedLabel = computed(() => {
  if (addedMethod.value === 'passkey')
    return t('KAPTION_TWO_STEP.BACKUP_DIALOG.ADDED.PASSKEY');
  if (addedMethod.value === 'whatsapp')
    return t('KAPTION_TWO_STEP.BACKUP_DIALOG.ADDED.WHATSAPP');
  if (addedMethod.value === 'authenticator')
    return t('KAPTION_TWO_STEP.BACKUP_DIALOG.ADDED.AUTHENTICATOR');
  return '';
});

const formatted = code =>
  code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;

const asText = () => {
  const email = store.getters.getCurrentUser?.email || '';
  return [
    t('KAPTION_TWO_STEP.BACKUP_DIALOG.FILE_HEADER', { email }),
    '',
    ...codes.value.map(formatted),
  ].join('\n');
};

const open = ({ backupCodes, method = '' }) => {
  codes.value = backupCodes || [];
  addedMethod.value = method;
  saved.value = false;
  dialogRef.value?.open();
};

const close = () => {
  codes.value = [];
  dialogRef.value?.close();
};

const copyAll = async () => {
  await copyTextToClipboard(asText());
  useAlert(t('KAPTION_TWO_STEP.BACKUP_DIALOG.COPIED'));
};

const download = () => {
  const blob = new Blob([asText()], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'rumo-backup-codes.txt';
  link.click();
  URL.revokeObjectURL(url);
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @confirm="saved && close()"
    @close="emit('closed')"
  >
    <div class="flex flex-col gap-5" data-testid="kpt-two-step-backup-dialog">
      <div class="flex flex-col gap-2">
        <div
          v-if="addedLabel"
          class="flex items-center gap-2 text-[13px] text-n-teal-11"
        >
          <Icon icon="i-lucide-circle-check" class="size-4" />
          {{ addedLabel }}
        </div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('KAPTION_TWO_STEP.BACKUP_DIALOG.TITLE') }}
        </h3>
        <p class="text-sm leading-relaxed text-n-slate-11">
          {{ $t('KAPTION_TWO_STEP.BACKUP_DIALOG.DESC') }}
        </p>
      </div>
      <div
        class="grid grid-cols-2 p-4 font-mono text-sm text-center border rounded-lg gap-x-4 gap-y-2 border-n-weak bg-n-alpha-black2/50 text-n-slate-12"
        data-testid="kpt-two-step-backup-codes"
      >
        <span v-for="code in codes" :key="code">{{ formatted(code) }}</span>
      </div>
      <div class="flex gap-2">
        <Button
          type="button"
          faded
          sm
          icon="i-lucide-download"
          :label="$t('KAPTION_TWO_STEP.BACKUP_DIALOG.DOWNLOAD')"
          @click="download"
        />
        <Button
          type="button"
          faded
          sm
          icon="i-lucide-copy"
          :label="$t('KAPTION_TWO_STEP.BACKUP_DIALOG.COPY_ALL')"
          @click="copyAll"
        />
      </div>
      <label
        class="flex items-start gap-2.5 text-sm cursor-pointer text-n-slate-12"
      >
        <input
          v-model="saved"
          type="checkbox"
          class="size-4 mt-0.5 accent-n-brand"
          data-testid="kpt-two-step-backup-saved"
        />
        {{ $t('KAPTION_TWO_STEP.BACKUP_DIALOG.SAVED_CHECK') }}
      </label>
    </div>
    <template #footer>
      <Button
        type="submit"
        class="w-full"
        :label="$t('KAPTION_TWO_STEP.BACKUP_DIALOG.DONE')"
        :disabled="!saved"
        data-testid="kpt-two-step-backup-done"
      />
    </template>
  </Dialog>
</template>
